/* eslint-disable @atlaskit/volt-strict-mode/no-multiple-exports */
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Preserves the existing UUID implementation.
import { v4 as createUUID } from 'uuid';

import coinflip from '../coinflip';
import type {
	AbortReasonType,
	ApdexType,
	BM3Event,
	CustomData,
	CustomTiming,
	InteractionError,
	HoldActive,
	HoldInfo,
	InteractionMetrics,
	InteractionType,
	MetricVariantCategory,
	LifecycleMarkType,
	LoadProfilerEventInfo,
	Mark,
	MarkType,
	PostInteractionLogOutput,
	ReactProfilerTiming,
	RequestInfo,
	SegmentInfo,
	Segment3pTimingEntry,
	Span,
	SpanType,
} from '../common';
import type { PreloadInfo } from '../common/common/types';
import { sanitizeTimingName } from '../common/utils/timing-name';
import {
	getAwaitBM3TTIList,
	getCapabilityRate,
	getConfig,
	getFinishInteractionOnTransition,
	getInteractionTimeout,
	getPostInteractionRate,
	getReactHydrationStats,
	getSelectorConfig,
	shouldUseRawDataThirdPartyBehavior,
} from '../config';
import { onSearchPageInteractionComplete } from '../create-extra-search-page-interaction-payload/on-search-page-interaction-complete';
import { sanitizeUfoName } from '../create-payload/common/utils/sanitize-ufo-name';
import { stringifyLabelStackFully } from '../create-payload/common/utils/stringify-label-stack-fully';
import type { TraceIdContext } from '../experience-trace-id-context';
import { clearActiveTrace } from '../experience-trace-id-context/clear-active-trace';
import {
	allFeatureFlagsAccessed,
	currentFeatureFlagsAccessed,
	type FeatureFlagValue,
} from '../feature-flags-accessed';
import type { LabelStack, SegmentLabel } from '../interaction-context';
import { getInteractionId } from '../interaction-id-context/getInteractionId';
import { BACKEND_RESOURCE_TIMING_INITIATOR_TYPES } from '../resource-timing/common/utils/resource-timing-initiator-types';
import { flushSsrRenderProfilerTraces } from '../segment/ssr-render-profiler/flush-traces';
import { newVCObserver } from '../vc/newVCObserver';
import { type VCObserverInterface } from '../vc/types';

import { interactions } from './common/constants';
import PostInteractionLog from './post-interaction-log';

export type {
	InteractionMetrics,
	LifecycleMarkType,
	Span,
	Mark,
	MarkType,
	InteractionType,
	AbortReasonType,
	ReactProfilerTiming,
	RequestInfo,
	ApdexType,
	CustomData,
	CustomTiming,
	InteractionError,
};

export type PreviousInteractionLogType = {
	id: string | undefined;
	name: string | undefined;
	type: string | undefined;
	isAborted: boolean | undefined;
	timestamp: number | undefined;
};

export const PreviousInteractionLog: PreviousInteractionLogType = {
	id: undefined,
	name: undefined,
	type: undefined,
	isAborted: undefined,
	timestamp: undefined,
};

export const postInteractionLog: PostInteractionLog = new PostInteractionLog();
const interactionQueue: { id: string; data: InteractionMetrics }[] = [];
const segmentCache = new Map<string, SegmentInfo>();
export const segmentUnmountCache: Map<string, number> = new Map<string, number>(); // Temporarily store segment unmount counts

interface SegmentObserver {
	onAdd: (segment: SegmentInfo) => void;
	onRemove: (segment: SegmentInfo) => void;
}
const segmentObservers: SegmentObserver[] = [];

export function getActiveInteraction(): InteractionMetrics | undefined {
	const interactionId = getInteractionId();
	if (!interactionId.current) {
		return;
	}
	return interactions.get(interactionId.current);
}

function isPerformanceTracingEnabled() {
	return (
		getConfig()?.enableAdditionalPerformanceMarks ||
		window.__REACT_UFO_ENABLE_PERF_TRACING ||
		process.env.NODE_ENV !== 'production'
	);
}

let performanceEventObserver: PerformanceObserver | undefined;

export const getPerformanceObserver = (): PerformanceObserver => {
	performanceEventObserver =
		performanceEventObserver ||
		new PerformanceObserver((entries: PerformanceObserverEntryList) => {
			const list = entries.getEntries();
			for (let entry of list) {
				if (entry.name === 'click') {
					setInteractionPerformanceEvent(entry as PerformanceEventTiming);
				}
			}
		});
	return performanceEventObserver;
};

export const setInteractionPerformanceEvent = (entry: PerformanceEventTiming): void => {
	const interaction = getActiveInteraction();
	if (interaction?.type === 'press') {
		const responsiveness = interaction.responsiveness || {};
		// if happens there is another event interaction that has started after
		// the initial one, we don't want to replace the values if they have already
		// been set up
		responsiveness.experimentalInputToNextPaint =
			responsiveness.experimentalInputToNextPaint || entry.duration;
		responsiveness.inputDelay =
			responsiveness.experimentalInputToNextPaint || entry.processingStart - entry.startTime;
		interaction.responsiveness = responsiveness;
		// if the entry start time is lower than the one in the interaction
		// it means the interaction start time is not accurate, we assign
		// this value which will match the timestamp in the event
		if (entry.startTime < interaction.start) {
			interaction.start = entry.startTime;
		}
	}
};

function labelStackToString(labelStack: LabelStack | null | undefined, name?: string) {
	const stack = [...(labelStack ?? [])];
	if (name) {
		stack.push({ name });
	}
	return stack.map((l) => l.name)?.join('/');
}

function labelStackToIdString(labelStack: LabelStack | null | undefined) {
	return labelStack
		?.map((l) => ('segmentId' in l ? `${l.name}:${l.segmentId}` : `${l.name}`))
		?.join('/');
}

function addSegmentObserver(observer: SegmentObserver) {
	segmentObservers.push(observer);

	for (const segmentInfo of segmentCache.values()) {
		observer.onAdd(segmentInfo);
	}
}

function removeSegmentObserver(observer: SegmentObserver) {
	const index = segmentObservers.findIndex((obs) => obs === observer);

	if (index !== -1) {
		segmentObservers.splice(index, 1);
	}
}

export function remove(interactionId: string): void {
	interactions.delete(interactionId);
}

export function updatePageLoadInteractionName(
	ufoName: string,
	routeName: string | null | undefined = ufoName,
): void {
	const interaction = getActiveInteraction();
	if (!interaction || (interaction.type !== 'page_load' && interaction.type !== 'transition')) {
		return;
	}
	interaction.ufoName = ufoName;
	interaction.routeName = routeName;
}

export function addMetadata(interactionId: string, data: Record<string, unknown>): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		Object.keys(data).forEach((key) => {
			interaction.metaData[key] = data[key];
		});
	}
}

export function addCustomData(
	interactionId: string,
	labelStack: LabelStack,
	data: CustomData,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		Object.keys(data).forEach((i) => {
			interaction.customData.push({ labelStack, data: { [i]: data[i] } });
		});
	}
}

/**
 * Records a diagnostic breadcrumb for a third-party segment that was intentionally excluded from all
 * metric windows (e.g. a Forge background module rendered with `excludeFromMetrics`). The data is a
 * raw keyed object (same shape as `customData`), stored in its own `excluded3pSegmentData` map keyed
 * by `segmentId`. This creates no hold and is never read by any metric computation.
 */
export function addExcluded3pSegment(
	interactionId: string,
	segmentId: string,
	data: CustomData,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		(interaction.excluded3pSegmentData ??= {})[segmentId] = {
			segmentId,
			...interaction.excluded3pSegmentData[segmentId],
			...data,
		};
	}
}

export function addSegmentExtraData(
	interactionId: string,
	segmentId: string,
	data: Record<string, string | undefined>,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		if (!interaction.segmentExtraData) {
			interaction.segmentExtraData = {};
		}
		interaction.segmentExtraData[segmentId] = {
			...interaction.segmentExtraData[segmentId],
			...data,
		};
	}
}

/**
 * Builds a stable cross-segment dedup key for a segment3pTimings entry.
 *
 * For `resource-timing`, query-stripped backend labels + timing fields identify a request without
 * depending on query parameters. Shared CSS/JS asset timings dedupe across segments, but backend
 * fetch/XHR entries are scoped by `segmentId` because each iframe can make its own backend request
 * to the same URL.
 *
 * For all other labels the data objects are small (a handful of fields), so we use a full
 * JSON fingerprint which is cheap and collision-free.
 */
function buildCrossSegmentDedupKey(entry: Segment3pTimingEntry, segmentId: string): string {
	if (entry.label === 'resource-timing') {
		const {
			label: resourceLabel,
			startTime,
			duration,
			requestStart,
			fetchStart,
			ttfb,
			type,
		} = entry.data as Record<string, unknown>;
		if (BACKEND_RESOURCE_TIMING_INITIATOR_TYPES.has(String(type))) {
			// Backend calls are separate requests per segment; include extra timings and segment scope.
			return `resource-timing|${resourceLabel}|${startTime}|${duration}|${fetchStart}|${requestStart}|${ttfb}|${segmentId}`;
		}
		// Shared CSS/JS assets keep the original B3 key so they can dedupe across segments.
		return `resource-timing|${resourceLabel}|${startTime}|${duration}`;
	}
	return `${entry.label}|${JSON.stringify(entry.data)}`;
}

export function addIframeSegmentData(
	interactionId: string,
	segmentId: string,
	entry: Segment3pTimingEntry,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		// B3: dedup by content — drops identical entries within and across iframes.
		// Abort markers are excluded so every segment's abort is recorded.
		if (entry.label !== 'segment-timing-abort') {
			if (!interaction.segment3pCrossSegmentSeen) {
				interaction.segment3pCrossSegmentSeen = new Set();
			}
			const key = buildCrossSegmentDedupKey(entry, segmentId);
			if (interaction.segment3pCrossSegmentSeen.has(key)) {
				return;
			}
			interaction.segment3pCrossSegmentSeen.add(key);
		}

		if (!interaction.segment3pTimings) {
			interaction.segment3pTimings = {};
		}
		if (!interaction.segment3pTimings[segmentId]) {
			interaction.segment3pTimings[segmentId] = [];
		}
		interaction.segment3pTimings[segmentId].push(entry);
	}
}

export function addCohortingCustomData(
	interactionId: string,
	key: string,
	value: number | boolean | string | null | undefined,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction == null) {
		return;
	}

	// Allow null and undefined values
	if (value === null || value === undefined) {
		interaction.cohortingCustomData.set(key, value);
		return;
	}

	// Validate that the value is a primitive (number, boolean, or string)
	if (typeof value !== 'number' && typeof value !== 'boolean' && typeof value !== 'string') {
		return;
	}

	// Validate string length (max 50 characters)
	if (typeof value === 'string' && value.length > 50) {
		return;
	}

	interaction.cohortingCustomData.set(key, value);
}

export function addCustomTiming(
	interactionId: string,
	labelStack: LabelStack,
	data: CustomTiming,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const sanitizedData = Object.fromEntries(
			Object.entries(data).map(([key, timingData]) => [sanitizeTimingName(key), timingData]),
		) as CustomTiming;
		interaction.customTimings.push({ labelStack, data: sanitizedData });
		if (isPerformanceTracingEnabled()) {
			for (const [key, timingData] of Object.entries(sanitizedData)) {
				const { startTime, endTime } = timingData;
				try {
					// for Firefox 102 and older
					performance.measure(`🛸 ${labelStackToString(labelStack, key)} [custom_timing]`, {
						start: startTime,
						end: endTime,
						detail: {
							devtools: {
								track: '🛸 reactUFO detailed timings',
								color: 'tertiary-light',
							},
						},
					});
				} catch {
					// do nothing
				}
			}
		}
	}
}

export function addMark(
	interactionId: string,
	type: MarkType,
	name: string,
	labelStack: LabelStack | null,
	time: number = performance.now(),
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.marks.push({ type, name, labelStack, time });
	}
	if (isPerformanceTracingEnabled()) {
		performance.mark(`🛸 ${labelStackToString(labelStack, name)} [${type}]`, {
			startTime: time,
		});
	}
}

export function markFirstSegmentLoad(
	_interactionId: string,
	_labelStack: LabelStack | null,
	_time?: number,
): void {}

export function addMarkToAll(
	type: MarkType,
	name: string,
	labelStack: LabelStack | null,
	time: number = performance.now(),
): void {
	interactions.forEach((interaction) => {
		interaction.marks.push({ type, name, labelStack, time });
	});
	if (isPerformanceTracingEnabled()) {
		performance.mark(`🛸 ${labelStackToString(labelStack, name)} [${type}]`, {
			startTime: time,
		});
	}
}

export function addSpan(
	interactionId: string,
	type: SpanType,
	name: string,
	labelStack: LabelStack | null,
	start: number,
	end: number = performance.now(),
	size?: number,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.spans.push({ type, name, labelStack, start, end, size });
		if (isPerformanceTracingEnabled()) {
			try {
				// for Firefox 102 and older
				performance.measure(`🛸 ${labelStackToString(labelStack, name)} [${type}]`, {
					start,
					end,
					detail: {
						devtools: {
							track: '🛸 reactUFO detailed timings',
							color: 'secondary',
						},
					},
				});
			} catch {
				// do nothing
			}
		}
	}
}

export function addSpanToAll(
	type: SpanType,
	name: string,
	labelStack: LabelStack | null,
	start: number,
	end: number = performance.now(),
	size = 0,
): void {
	interactions.forEach((interaction) => {
		interaction.spans.push({ type, name, labelStack, start, end, size });
	});
	if (isPerformanceTracingEnabled()) {
		try {
			// for Firefox 102 and older
			performance.measure(`🛸 ${labelStackToString(labelStack, name)} [${type}]`, {
				start,
				end,
				detail: {
					devtools: {
						track: '🛸 reactUFO detailed timings',
						color: 'secondary',
					},
				},
			});
		} catch {
			// do nothing
		}
	}
}

export function addPreload(moduleId: string, timestamp: number): void {
	addMarkToAll('bundle_preload', moduleId, null, timestamp);
}

export function addLoad(identifier: string, start: number, end: number): void {
	addSpanToAll('bundle_load', identifier, null, start, end - start);
}

const moduleLoadingRequests: Record<
	string,
	{
		start: number;
		timeoutId: ReturnType<typeof setTimeout> | number | undefined;
	}
> = {};

export function extractModuleName(input: string): string {
	let result = input ?? '';

	result = result.replace(/^\.\/src\/packages\//, '');
	result = result.replace(/^\.\/node_modules\//, '');
	result = result.replace(/(\/src)?\/(index|main)\.(tsx|ts|js|jsx)$/, '');

	return result;
}

function addHoldCriterion(id: string, labelStack: LabelStack, name: string, startTime: number) {
	if (!window.__CRITERION__?.addUFOHold) {
		return;
	}
	window.__CRITERION__.addUFOHold(id, labelStackToString(labelStack), name, startTime);
}

function removeHoldCriterion(id: string) {
	if (!window.__CRITERION__?.removeUFOHold) {
		return;
	}
	window.__CRITERION__.removeUFOHold(id);
}

export function addHold(
	interactionId: string,
	labelStack: LabelStack,
	name: string,
	_experimental: boolean,
	holdStartedAt?: number,
): () => void {
	const interaction = interactions.get(interactionId);
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const id = createUUID();

	if (!interaction) {
		// add hold timestamp to post interaction log if interaction is complete
		postInteractionLog.addHoldInfo(labelStack, name, holdStartedAt ?? performance.now());
	}

	if (interaction != null) {
		const start = holdStartedAt ?? performance.now();
		const holdActive = { labelStack, name, start };

		if (shouldMoveHoldToExtendedBucket(interaction, holdActive)) {
			ensureExtendedHoldActive(interaction).set(id, { ...holdActive, start });
		} else {
			interaction.holdActive.set(id, { ...holdActive, start });
			addHoldCriterion(id, labelStack, name, start);
		}

		return () => {
			const end = performance.now();
			if (isPerformanceTracingEnabled()) {
				try {
					// for Firefox 102 and older
					performance.measure(`🛸 ${labelStackToString(labelStack, name)} [hold]`, {
						start,
						end,
						detail: {
							devtools: {
								track: '🛸 reactUFO detailed timings',
								color: 'secondary-light',
							},
						},
					});
				} catch {
					// do nothing
				}
			}
			removeHoldCriterion(id);
			const currentInteraction = interactions.get(interactionId);
			const currentHold = interaction.holdActive.get(id);
			if (currentInteraction != null) {
				if (currentHold != null) {
					currentInteraction.holdInfo.push({ ...currentHold, end });
					interaction.holdActive.delete(id);
				}

				if (interaction.hold3pActive) {
					const current3pHold = interaction.hold3pActive.get(id);
					if (current3pHold != null) {
						recordMetricVariantCategoryEnd(currentInteraction, current3pHold.labelStack, end);
						if (!currentInteraction.hold3pInfo) {
							currentInteraction.hold3pInfo = [];
						}
						currentInteraction.hold3pInfo.push({ ...current3pHold, end });
						interaction.hold3pActive.delete(id);
					}
				}
			}
		};
	}
	return () => {};
}

export function addHoldByID(
	interactionId: string,
	labelStack: LabelStack,
	name: string,
	id: string,
	ignoreOnSubmit?: boolean,
): () => void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const start = performance.now();
		const holdActive = { labelStack, name, start, ignoreOnSubmit };
		if (shouldMoveHoldToExtendedBucket(interaction, holdActive)) {
			ensureExtendedHoldActive(interaction).set(id, holdActive);
		} else {
			interaction.holdActive.set(id, holdActive);
			addHoldCriterion(id, labelStack, name, start);
		}
	}
	return (): void => {};
}

/**
 * Records a completed third-party hold with explicit start and end timestamps.
 * Use this when the hold timing is known upfront (e.g. from React Profiler's
 * actualStartTime and commitTime) rather than being captured at call time.
 */
export function addCompletedHold(
	interactionId: string,
	labelStack: LabelStack,
	name: string,
	start: number,
	end: number,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		// `recordMetricVariantCategoryEnd` skips excluded (background-script) holds, so the completed
		// hold is retained in `hold3pInfo` for observability but never contributes a third-party
		// category end.
		recordMetricVariantCategoryEnd(interaction, labelStack, end);
		ensureExtendedHoldInfo(interaction).push({ labelStack, name, start, end });
	}
}

export function removeHoldByID(interactionId: string, id: string): void {
	const interaction = interactions.get(interactionId);

	if (interaction != null) {
		const end = performance.now();
		const currentInteraction = interactions.get(interactionId);
		const currentHold = interaction.holdActive.get(id);
		if (currentInteraction != null && currentHold != null) {
			if (shouldMoveHoldToExtendedBucket(currentInteraction, currentHold)) {
				ensureExtendedHoldInfo(currentInteraction).push({ ...currentHold, end });
			} else {
				currentInteraction.holdInfo.push({ ...currentHold, end });
			}
			interaction.holdActive.delete(id);
			removeHoldCriterion(id);
		}

		if (interaction.hold3pActive) {
			const current3pHold = interaction.hold3pActive.get(id);
			if (currentInteraction != null && current3pHold != null) {
				recordMetricVariantCategoryEnd(currentInteraction, current3pHold.labelStack, end);
				if (!currentInteraction.hold3pInfo) {
					currentInteraction.hold3pInfo = [];
				}
				currentInteraction.hold3pInfo.push({ ...current3pHold, end });
				interaction.hold3pActive.delete(id);
			}
		}
	}
}

export function getCurrentInteractionType(interactionId: string): InteractionType | null {
	const interaction = interactions.get(interactionId);
	if (interaction) {
		return interaction.type;
	}
	return null;
}

export function registerPreloadInfo(
	interactionId: string,
	info: {
		source: string;
		preloadStartedAt: number;
		adoptedAt: number;
	},
): () => void {
	const interaction = interactions.get(interactionId);
	if (interaction == null) {
		return () => {};
	}

	const preloadRow: PreloadInfo = {
		source: info.source,
		preloadStartedAt: info.preloadStartedAt,
		adoptedAt: info.adoptedAt,
	};
	interaction.preloadInfo.push(preloadRow);

	const releaseHold = addHold(
		interactionId,
		[],
		`preload:${info.source}`,
		false,
		interaction.start,
	);

	let released = false;
	return () => {
		if (released) {
			return;
		}
		released = true;
		preloadRow.settledAt = performance.now();
		releaseHold();
	};
}

export function adoptPreloadHoldForActiveInteraction(args: {
	experienceKey: string;
	preloadKey?: string;
	source: string;
	preloadStartedAt: number;
	adoptedAt: number;
}): (() => void) | null {
	const interaction = getActiveInteraction();
	if (
		interaction == null ||
		interaction.ufoName !== args.experienceKey ||
		(args.preloadKey !== undefined &&
			interaction.preloadKey !== undefined &&
			interaction.preloadKey !== args.preloadKey)
	) {
		return null;
	}
	return registerPreloadInfo(interaction.id, {
		source: args.source,
		preloadStartedAt: args.preloadStartedAt,
		adoptedAt: args.adoptedAt,
	});
}

let onInteractionStartForPreloadHolds:
	| ((interactionId: string, ufoName: string, preloadKey?: string) => void)
	| null = null;

/** Adopts pre-interaction preloads without importing `preload-hold` here. */
export function setPreloadHoldAdoptionHook(
	hook: ((interactionId: string, ufoName: string, preloadKey?: string) => void) | null,
): void {
	onInteractionStartForPreloadHolds = hook;
}

export const ModuleLoadingProfiler = {
	onPreload(moduleId: string, _priority?: number): void {
		addPreload(extractModuleName(moduleId), performance.now());
	},
	onLoadStart(info: LoadProfilerEventInfo): void {
		const timeoutId = setTimeout(() => {
			delete moduleLoadingRequests[info.identifier];
		}, 30000);
		const request = {
			start: performance.now(),
			timeoutId,
		};
		moduleLoadingRequests[info.identifier] = request;
	},
	onLoadComplete(info: LoadProfilerEventInfo): void {
		const request = moduleLoadingRequests[info.identifier];
		if (request) {
			clearTimeout(request.timeoutId);
			delete moduleLoadingRequests[info.identifier];
			addLoad(extractModuleName(info.identifier), request.start, performance.now());
		}
	},
	placeholderFallBackMounted(id: string, moduleId: string): void {
		const interactionId = getInteractionId();
		const currentInteractionId = interactionId.current;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		addHoldByID(currentInteractionId!, [], moduleId, id);
	},
	placeholderFallBackUnmounted(id: string): void {
		const interactionId = getInteractionId();
		const currentInteractionId = interactionId.current;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		removeHoldByID(currentInteractionId!, id);
	},
};

export function addError(
	interactionId: string,
	name: string,
	labelStack: LabelStack | null,
	errorType: string,
	errorMessage: string,
	errorStack?: string,
	forcedError?: boolean,
	errorHash?: string,
	errorStatusCode?: number,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.errors.push({
			name,
			labelStack,
			errorType,
			errorMessage,
			errorStack,
			forcedError,
			errorHash,
			errorStatusCode,
		});
	}
}

export function addErrorToAll(
	name: string,
	labelStack: LabelStack | null,
	errorType: string,
	errorMessage: string,
	errorStack?: string,
	errorHash?: string,
	errorStatusCode?: number,
): void {
	interactions.forEach((interaction) => {
		interaction.errors.push({
			name,
			labelStack,
			errorType,
			errorMessage,
			errorStack,
			errorHash,
			errorStatusCode,
		});
	});
}

export function addProfilerTimings(
	interactionId: string,
	labelStack: LabelStack,
	type: 'mount' | 'update' | 'nested-update',
	actualDuration: number,
	baseDuration: number,
	startTime: number,
	commitTime: number,
): void {
	if (isPerformanceTracingEnabled()) {
		try {
			// for Firefox 102 and older
			performance.measure(`🛸 ${labelStackToString(labelStack)} [react-profiler] ${type}`, {
				start: startTime,
				duration: actualDuration,
				detail: {
					devtools: {
						track: '🛸 reactUFO detailed timings',
						color: 'secondary-dark',
					},
				},
			});
		} catch {
			// do nothing
		}
	}
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.reactProfilerTimings.push({
			labelStack,
			type,
			actualDuration,
			baseDuration,
			startTime,
			commitTime,
		});
	} else if (getConfig()?.postInteractionLog?.enabled) {
		postInteractionLog.addProfilerTimings(
			labelStack,
			type,
			actualDuration,
			baseDuration,
			startTime,
			commitTime,
		);
	}
}

function pushToQueue(id: string, data: InteractionMetrics) {
	interactionQueue.push({ id, data });
}

let handleInteraction = pushToQueue;

const isKnownMetricVariantCategory = (type: unknown): type is MetricVariantCategory =>
	type === 'third-party' || type === 'gen-ai';

const hasMetricVariantCategory = (
	labelStack: LabelStack | null | undefined,
	category: MetricVariantCategory,
): boolean => labelStack?.some((label) => 'type' in label && label.type === category) ?? false;

const hasAnyMetricVariantCategory = (labelStack: LabelStack | null | undefined): boolean =>
	labelStack?.some((label) => 'type' in label && isKnownMetricVariantCategory(label.type)) ?? false;

/**
 * A hold is fully excluded from every metric window (standard and include-third-party) and from
 * completion gating iff its labelStack contains a single label that is BOTH `type: 'third-party'`
 * AND `excludeFromMetrics: true`. Requiring both on the same label guarantees exclusion can only
 * ever apply to third-party work, never first-party. Such holds are dropped at creation time, so
 * they never enter `holdActive`/`hold3pActive`, never block completion.
 */
const isExcludedThirdPartyHold = (labelStack: LabelStack | null | undefined): boolean =>
	labelStack?.some(
		(label) =>
			'type' in label &&
			label.type === 'third-party' &&
			'excludeFromMetrics' in label &&
			label.excludeFromMetrics === true,
	) ?? false;

const getMetricVariantCategories = (
	labelStack: LabelStack | null | undefined,
): MetricVariantCategory[] => {
	const categories = new Set<MetricVariantCategory>();
	labelStack?.forEach((label) => {
		if ('type' in label && isKnownMetricVariantCategory(label.type)) {
			categories.add(label.type);
		}
	});
	return [...categories];
};

const hasMetricVariantCategoryInHoldData = (
	interaction: InteractionMetrics,
	category: MetricVariantCategory,
): boolean =>
	interaction.holdInfo.some((hold) => hasMetricVariantCategory(hold.labelStack, category)) ||
	[...(interaction.holdActive.values() ?? [])].some((hold) =>
		hasMetricVariantCategory(hold.labelStack, category),
	) ||
	[...(interaction.hold3pInfo ?? [])].some((hold) =>
		hasMetricVariantCategory(hold.labelStack, category),
	) ||
	[...(interaction.hold3pActive?.values() ?? [])].some((hold) =>
		hasMetricVariantCategory(hold.labelStack, category),
	);

const recordMetricVariantCategoryEnd = (
	interaction: InteractionMetrics,
	labelStack: LabelStack | null | undefined,
	end: number,
): void => {
	// Excluded third-party holds (e.g. Forge background scripts) still live in `hold3pActive` and
	// gate completion like any third-party hold, but they must never contribute a third-party
	// category end. Skipping here covers every caller at once: the normal-release closure in
	// `addHold`, `removeHoldByID`, `addCompletedHold`, and the abort/timeout iterator
	// `recordActiveMetricVariantCategoryEnds`.
	if (isExcludedThirdPartyHold(labelStack)) {
		return;
	}

	const categories = getMetricVariantCategories(labelStack);
	if (categories.length === 0) {
		return;
	}

	interaction.metricCategoryEnds = interaction.metricCategoryEnds ?? {};
	categories.forEach((category) => {
		interaction.metricCategoryEnds![category] = Math.max(
			interaction.metricCategoryEnds![category] ?? 0,
			end,
		);
	});
};

const recordActiveMetricVariantCategoryEnds = (
	interaction: InteractionMetrics,
	end: number,
): void => {
	interaction.hold3pActive?.forEach((hold) => {
		recordMetricVariantCategoryEnd(interaction, hold.labelStack, end);
	});
};

const hasActiveMetricVariantCategory = (
	interaction: InteractionMetrics,
	category: MetricVariantCategory,
): boolean =>
	[...(interaction.hold3pActive?.values() ?? [])].some((hold) =>
		hasMetricVariantCategory(hold.labelStack, category),
	);

function shouldMoveHoldToExtendedBucket(
	interaction: InteractionMetrics,
	hold: Pick<HoldActive, 'start' | 'labelStack'>,
): boolean {
	// Excluded third-party holds (e.g. Forge background scripts) are routed to the extended
	// (`hold3pActive`) bucket exactly like any other third-party hold: they carry `type: 'third-party'`
	// so `hasAnyMetricVariantCategory` is true. They therefore keep gating completion (which preserves
	// the standard-bucket VC parity), and are excluded ONLY at the third-party accounting sites
	// (`recordMetricVariantCategoryEnd` and the `end3p` computation via `getNonExcludedThirdPartyEnd`).
	return (
		hasAnyMetricVariantCategory(hold.labelStack) ||
		(interaction.end !== 0 && hold.start > interaction.end)
	);
}

/**
 * The third-party end marker (`end3p`), used as the `include-third-party` window end whenever no
 * genuine third-party category end was recorded, must reflect only NON-excluded third-party work.
 *
 * `currentTime` is the timestamp the caller would otherwise have used verbatim (e.g. the abort
 * time, or the interaction end on the success path). If there is any genuine (non-excluded)
 * third-party hold that is still ACTIVE at this point, that work legitimately extends to
 * `currentTime`, so we keep it. Otherwise the only active third-party holds are excluded
 * background scripts, which must NOT drag the marker, so we fall back to the latest end among the
 * already-released non-excluded holds, clamped to at least `interactionEnd`. This preserves the
 * pre-fix behaviour for real third-party work while preventing background scripts from inflating
 * `end3p` / the include-third-party window.
 *
 * Note the released entries in `hold3pInfo` are `HoldActive & { end: number }`, so we read `h.end`.
 */
const getNonExcludedThirdPartyEnd = (
	interaction: InteractionMetrics,
	currentTime: number,
	interactionEnd: number,
): number => {
	const activeHolds = [...(interaction.hold3pActive?.values() ?? [])];
	const releasedHolds = interaction.hold3pInfo ?? [];

	// If this interaction never involved an excluded (background-script) hold at all
	// (none active AND none released), it is unaffected by an exclusion logic.
	// The derivation below only runs when at least one excluded hold actually exists.
	const hasAnyExcludedThirdParty =
		activeHolds.some((hold) => isExcludedThirdPartyHold(hold.labelStack)) ||
		releasedHolds.some((hold) => isExcludedThirdPartyHold(hold.labelStack));
	if (!hasAnyExcludedThirdParty) {
		return currentTime;
	}

	// If a genuine (non-excluded) third-party hold is still active, the interaction is legitimately
	// being kept open by real third-party work, so the marker is the current time
	const hasActiveNonExcludedThirdParty = activeHolds.some(
		(hold) => !isExcludedThirdPartyHold(hold.labelStack),
	);
	if (hasActiveNonExcludedThirdParty) {
		return currentTime;
	}

	// Otherwise, only excluded background-script holds (if any) remain active. `currentTime` may have
	// been pushed out purely by a background script, so derive the marker from the latest genuine
	// (non-excluded) third-party work instead, floored to the interaction end so it is never reported
	// earlier than the interaction itself and a background script can never drag it late.
	return Math.max(
		interactionEnd,
		...releasedHolds
			.filter((hold) => !isExcludedThirdPartyHold(hold.labelStack))
			.map((hold) => hold.end),
	);
};

function ensureExtendedHoldActive(interaction: InteractionMetrics): Map<string, HoldActive> {
	interaction.hold3pActive = interaction.hold3pActive ?? new Map();
	return interaction.hold3pActive;
}

function ensureExtendedHoldInfo(interaction: InteractionMetrics): HoldInfo[] {
	interaction.hold3pInfo = interaction.hold3pInfo ?? [];
	return interaction.hold3pInfo;
}

function moveLateActiveHoldsToExtendedBucket(interaction: InteractionMetrics): void {
	if (interaction.end === 0) {
		return;
	}

	const holdsToMove = [...interaction.holdActive.entries()].filter(([, hold]) =>
		shouldMoveHoldToExtendedBucket(interaction, hold),
	);

	holdsToMove.forEach(([id, hold]) => {
		ensureExtendedHoldActive(interaction).set(id, hold);
		interaction.holdActive.delete(id);
	});
}

function ensureMetricWindows(interaction: InteractionMetrics): void {
	if (interaction.end === 0) {
		return;
	}

	const hasGenAI = hasMetricVariantCategoryInHoldData(interaction, 'gen-ai');
	const hasThirdParty = hasMetricVariantCategoryInHoldData(interaction, 'third-party');
	const includeThirdPartyEnd =
		interaction.metricCategoryEnds?.['third-party'] !== undefined
			? Math.max(interaction.metricCategoryEnds['third-party'], interaction.end)
			: interaction.end3p;
	const includeGenAIEnd =
		interaction.metricCategoryEnds?.['gen-ai'] !== undefined
			? Math.max(interaction.metricCategoryEnds['gen-ai'], interaction.end)
			: undefined;

	interaction.metricWindows = {
		...interaction.metricWindows,
		standard: {
			start: interaction.start,
			end: interaction.end,
			includeCategories: [],
			excludeCategories: ['third-party', 'gen-ai'],
		},
	};

	if (includeThirdPartyEnd !== undefined && (hasThirdParty || !hasGenAI)) {
		interaction.metricWindows['include-third-party'] = {
			start: interaction.start,
			end: includeThirdPartyEnd,
			includeCategories: ['third-party'],
			excludeCategories: [],
		};
	}

	if (hasGenAI && includeGenAIEnd !== undefined) {
		interaction.metricWindows['include-gen-ai'] = {
			start: interaction.start,
			end: includeGenAIEnd,
			includeCategories: ['gen-ai'],
			excludeCategories: [],
		};
	}
}

function addLifecycleObservation(
	interaction: InteractionMetrics,
	observation: NonNullable<InteractionMetrics['lifecycleObservations']>[number],
): void {
	interaction.lifecycleObservations = interaction.lifecycleObservations ?? [];
	interaction.lifecycleObservations.push(observation);
}

function callCleanUpCallbacks(interaction: InteractionMetrics) {
	interaction.cleanupCallbacks.reverse().forEach((cleanUpCallback) => {
		cleanUpCallback();
	});
}

function finishInteraction(
	id: string,
	data: InteractionMetrics,
	endTime: number = performance.now(),
) {
	data.end = endTime;
	ensureMetricWindows(data);
	try {
		// for Firefox 102 and older
		performance.measure(`🛸 [${data.type}] ${data.ufoName} [ttai]`, {
			start: data.start,
			end: data.end,
			detail: {
				devtools: {
					track: 'main metrics',
					trackGroup: '🛸 reactUFO metrics',
					color: 'tertiary',
				},
			},
		});

		performance.mark(`TTAI`, {
			startTime: data.end,
			detail: {
				devtools: {
					dataType: 'marker',
				},
			},
		});
	} catch {
		// do nothing
	}
	if (data.featureFlags) {
		data.featureFlags.during = Object.fromEntries(currentFeatureFlagsAccessed);
	}
	clearActiveTrace();
	callCleanUpCallbacks(data);
	flushSsrRenderProfilerTraces();
	if (data.type === 'page_load') {
		data.hydration = getReactHydrationStats();
	}

	// By this time, stop the post interaction log observer if coinflip rate is 0
	if (!coinflip(getPostInteractionRate(sanitizeUfoName(data.ufoName), data.type))) {
		postInteractionLog.stopVCObserver();
	}

	remove(id);

	PreviousInteractionLog.id = data.id;
	PreviousInteractionLog.type = data.type;
	PreviousInteractionLog.timestamp = data.end;
	PreviousInteractionLog.name = data.ufoName || 'unknown';
	PreviousInteractionLog.isAborted = data.abortReason != null;
	if (data.ufoName) {
		handleInteraction(id, data);
	}

	if (isPerformanceTracingEnabled()) {
		const profilerTimingMap = new Map<
			string,
			{ labelStack: LabelStack; start?: number; end?: number }
		>();
		data.reactProfilerTimings.forEach((profilerTiming) => {
			const labelStackId = labelStackToIdString(profilerTiming.labelStack);
			if (labelStackId) {
				const timing = profilerTimingMap.get(labelStackId) ?? {
					labelStack: profilerTiming.labelStack,
				};
				timing.start =
					profilerTiming.startTime < (timing.start ?? Number.MAX_SAFE_INTEGER)
						? profilerTiming.startTime
						: timing.start;
				timing.end =
					profilerTiming.commitTime > (timing.end ?? Number.MIN_SAFE_INTEGER)
						? profilerTiming.commitTime
						: timing.end;
				profilerTimingMap.set(labelStackId, timing);
			}
		});
		try {
			// for Firefox 102 and older
			for (const [, { labelStack, start, end }] of profilerTimingMap.entries()) {
				performance.measure(`🛸 ${labelStackToString(labelStack)} [segment_ttai]`, {
					start,
					end,
					detail: {
						devtools: {
							track: '🛸 reactUFO detailed timings',
							color: 'secondary-dark',
						},
					},
				});
			}
		} catch {
			// do nothing
		}
	}

	try {
		// dispatch a global window event to notify the measure is completed
		window.dispatchEvent(
			new CustomEvent<InteractionMetrics>('UFO_FINISH_INTERACTION', { detail: data }),
		);
	} catch {
		// do nothing
	}
}

export function sinkInteractionHandler(
	sinkFn: (id: string, data: InteractionMetrics) => void,
): void {
	if (handleInteraction === pushToQueue) {
		handleInteraction = sinkFn;
		interactionQueue.forEach((interaction) => {
			sinkFn(interaction.id, interaction.data);
		});
		interactionQueue.length = 0;
	}
}

export function sinkPostInteractionLogHandler(
	sinkFn: (output: PostInteractionLogOutput) => void | Promise<void>,
): void {
	postInteractionLog.sinkHandler(sinkFn);
}

// a flag to prevent multiple submitting
let activeSubmitted = false;

export function tryComplete(interactionId: string, endTime?: number): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const noMoreActiveHolds = interaction.holdActive.size === 0;
		const shouldUseRawDataThirdParty = shouldUseRawDataThirdPartyBehavior(
			interaction.ufoName,
			interaction.type,
		);

		const postInteraction = async () => {
			if (getConfig()?.postInteractionLog?.enabled) {
				postInteractionLog.onInteractionComplete(interaction);
			}
			activeSubmitted = false;
		};

		const noMoreActiveMetricVariantHolds =
			interaction.hold3pActive?.size === 0 || interaction.hold3pActive === undefined;

		const shouldUseSeparatedMetricVariantBehavior =
			shouldUseRawDataThirdParty || hasActiveMetricVariantCategory(interaction, 'gen-ai');

		// If using separated metric variant behavior, wait for extended category holds to clear.
		// The backing fields still use their legacy 3P names because they are part of the existing
		// third-party payload contract, but this bucket now also drives client metric windows.
		if (shouldUseSeparatedMetricVariantBehavior) {
			// If there are no standard holds active, mark the interaction as successful
			// but don't finish until extended metric variant holds are cleared.
			if (noMoreActiveHolds && !noMoreActiveMetricVariantHolds) {
				// Mark interaction as successful by setting endTime, but don't finish yet
				if (endTime !== undefined && interaction.end === 0) {
					interaction.end = endTime;
				}
				moveLateActiveHoldsToExtendedBucket(interaction);
				ensureMetricWindows(interaction);
				// Wait for extended metric variant holds to clear before finishing.
				return;
			}

			// If all holds, including extended metric variant holds, are cleared, finish the interaction.
			if (noMoreActiveHolds && noMoreActiveMetricVariantHolds) {
				if (!activeSubmitted) {
					// Set the legacy third-party end marker when extended metric variant holds clear,
					// preserving existing 3P payload behavior while category-specific end times are
					// recorded separately in metricCategoryEnds. When a genuine third-party hold is still
					// active (or was the last to release), `getNonExcludedThirdPartyEnd` returns the same
					// clamped current time as before. When only excluded background-script holds remain,
					// the marker is derived from the non-excluded third-party holds and floored to the
					// interaction end, so a background script cannot drag it late.
					const currentTime = endTime ?? performance.now();
					interaction.end3p = getNonExcludedThirdPartyEnd(
						interaction,
						interaction.end !== 0 && currentTime < interaction.end ? interaction.end : currentTime,
						interaction.end !== 0 ? interaction.end : currentTime,
					);
					ensureMetricWindows(interaction);
					finishInteraction(
						interactionId,
						interaction,
						interaction.end !== 0 ? interaction.end : endTime,
					);

					if (
						getConfig()?.extraSearchPageInteraction?.enabled &&
						interaction.ufoName === getConfig()?.extraSearchPageInteraction?.searchPageMetricName
					) {
						onSearchPageInteractionComplete(interactionId, interaction);
					}

					activeSubmitted = true;
				}

				postInteraction();
			}
		} else {
			if (noMoreActiveHolds) {
				if (!activeSubmitted) {
					finishInteraction(interactionId, interaction, endTime);

					if (
						getConfig()?.extraSearchPageInteraction?.enabled &&
						interaction.ufoName === getConfig()?.extraSearchPageInteraction?.searchPageMetricName
					) {
						onSearchPageInteractionComplete(interactionId, interaction);
					}
					activeSubmitted = true;
				}

				postInteraction();
			}
		}
	}
}

function callCancelCallbacks(interaction: InteractionMetrics) {
	interaction.cancelCallbacks.reverse().forEach((cancelCallback) => {
		cancelCallback();
	});
}

export function abort(interactionId: string, abortReason: AbortReasonType): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const shouldUseRawDataThirdParty = shouldUseRawDataThirdPartyBehavior(
			interaction.ufoName,
			interaction.type,
		);
		const noMoreActiveHolds = interaction.holdActive.size === 0;
		const has3pHoldsActive = interaction.hold3pActive && interaction.hold3pActive.size > 0;
		const shouldUseSeparatedMetricVariantBehavior =
			shouldUseRawDataThirdParty || hasActiveMetricVariantCategory(interaction, 'gen-ai');

		// If only extended metric variant holds are active, finish as successful instead of aborting
		if (shouldUseSeparatedMetricVariantBehavior && noMoreActiveHolds && has3pHoldsActive) {
			const endTime = interaction.end !== 0 ? interaction.end : performance.now();
			// Excluded background-script holds that are still active at abort/timeout must not drag
			// `end3p` (and thus the include-third-party window) past the real interaction end. Derive it
			// from the non-excluded third-party holds only. `recordActiveMetricVariantCategoryEnds`
			// likewise skips excluded holds internally.
			interaction.end3p = getNonExcludedThirdPartyEnd(interaction, performance.now(), endTime);
			recordActiveMetricVariantCategoryEnds(interaction, interaction.end3p);
			addLifecycleObservation(interaction, {
				type: abortReason === 'timeout' ? 'timeout_expired' : 'page_unloaded',
				timestamp: interaction.end3p,
				activeHoldCount: interaction.hold3pActive?.size ?? 0,
			});
			ensureMetricWindows(interaction);
			finishInteraction(interactionId, interaction, endTime);
			postInteractionLog.reset();
			postInteractionLog.stopVCObserver();

			return;
		}

		callCancelCallbacks(interaction);
		interaction.abortReason = abortReason;
		addLifecycleObservation(interaction, {
			type: abortReason === 'timeout' ? 'timeout_expired' : 'page_unloaded',
			timestamp: performance.now(),
			activeHoldCount: interaction.holdActive.size + (interaction.hold3pActive?.size ?? 0),
		});
		finishInteraction(interactionId, interaction);
		postInteractionLog.reset();
		postInteractionLog.stopVCObserver();
	}
}

export function abortByNewInteraction(interactionId: string, interactionName: string): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const shouldUseRawDataThirdParty = shouldUseRawDataThirdPartyBehavior(
			interaction.ufoName,
			interaction.type,
		);
		const noMoreActiveHolds = interaction.holdActive.size === 0;
		const has3pHoldsActive = interaction.hold3pActive && interaction.hold3pActive.size > 0;
		const shouldUseSeparatedMetricVariantBehavior =
			shouldUseRawDataThirdParty || hasActiveMetricVariantCategory(interaction, 'gen-ai');

		// If only extended metric variant holds are active, finish as successful instead of aborting
		if (shouldUseSeparatedMetricVariantBehavior && noMoreActiveHolds && has3pHoldsActive) {
			const endTime = interaction.end !== 0 ? interaction.end : performance.now();
			// Excluded background-script holds still active at this point must not drag `end3p` (and
			// thus the include-third-party window) past the real interaction end. Derive it from the
			// non-excluded third-party holds only, clamped to at least the interaction end.
			interaction.end3p = getNonExcludedThirdPartyEnd(interaction, performance.now(), endTime);
			recordActiveMetricVariantCategoryEnds(interaction, interaction.end3p);
			addLifecycleObservation(interaction, {
				type: 'new_interaction_started',
				timestamp: interaction.end3p,
				triggerName: interactionName,
			});
			ensureMetricWindows(interaction);
			finishInteraction(interactionId, interaction, endTime);
			postInteractionLog.reset();
			postInteractionLog.stopVCObserver();

			return;
		}

		callCancelCallbacks(interaction);
		interaction.abortReason = 'new_interaction';
		interaction.abortedByInteractionName = interactionName;
		addLifecycleObservation(interaction, {
			type: 'new_interaction_started',
			timestamp: performance.now(),
			triggerName: interactionName,
		});
		finishInteraction(interactionId, interaction);
		postInteractionLog.reset();
		postInteractionLog.stopVCObserver();
	} else {
		// post-interaction log is active after interaction is aborted by new one
		postInteractionLog.reset();
		postInteractionLog.stopVCObserver();
	}
}

export function abortAll(abortReason: AbortReasonType, abortedByInteractionName?: string): void {
	const activeInteraction = getActiveInteraction();
	const finishInteractions = getFinishInteractionOnTransition();
	interactions.forEach((interaction, interactionId) => {
		const isActiveInteraction = activeInteraction === interaction;
		let hasFinished = interaction.holdActive.size === 0;

		if (
			isActiveInteraction &&
			abortReason === 'transition' &&
			interaction.type === 'press' &&
			finishInteractions?.includes(interaction.ufoName)
		) {
			hasFinished = true;
		}

		const shouldUseRawDataThirdParty = shouldUseRawDataThirdPartyBehavior(
			interaction.ufoName,
			interaction.type,
		);
		const noMoreActiveHolds = interaction.holdActive.size === 0;
		const has3pHoldsActive = interaction.hold3pActive && interaction.hold3pActive.size > 0;
		const shouldUseSeparatedMetricVariantBehavior =
			shouldUseRawDataThirdParty || hasActiveMetricVariantCategory(interaction, 'gen-ai');

		// If only extended metric variant holds are active, finish as successful instead of aborting
		if (shouldUseSeparatedMetricVariantBehavior && noMoreActiveHolds && has3pHoldsActive) {
			const endTime = interaction.end !== 0 ? interaction.end : performance.now();
			// Excluded background-script holds still active at transition/new-interaction abort must not
			// drag `end3p` (and thus the include-third-party window) past the real interaction end.
			// Derive it from the non-excluded third-party holds only, clamped to at least the end.
			interaction.end3p = getNonExcludedThirdPartyEnd(interaction, performance.now(), endTime);
			recordActiveMetricVariantCategoryEnds(interaction, interaction.end3p);
			addLifecycleObservation(interaction, {
				type: abortReason === 'transition' ? 'transition_started' : 'new_interaction_started',
				timestamp: interaction.end3p,
				triggerName: abortedByInteractionName,
			});
			ensureMetricWindows(interaction);
			finishInteraction(interactionId, interaction, endTime);
			postInteractionLog.reset();
			postInteractionLog.stopVCObserver();

			return;
		}

		if (!hasFinished) {
			callCancelCallbacks(interaction);
			interaction.abortReason = abortReason;
			if (abortedByInteractionName != null) {
				interaction.abortedByInteractionName = abortedByInteractionName;
			}
			addLifecycleObservation(interaction, {
				type: abortReason === 'transition' ? 'transition_started' : 'new_interaction_started',
				timestamp: performance.now(),
				triggerName: abortedByInteractionName,
			});
		}

		finishInteraction(interactionId, interaction);
		postInteractionLog.reset();
		postInteractionLog.stopVCObserver();
	});
}

export function addOnCancelCallback(id: string, cancelCallback: () => void): void {
	const interaction = interactions.get(id);

	interaction?.cancelCallbacks.push(cancelCallback);
}

export function addNewInteraction(
	interactionId: string,
	ufoName: string,
	type: InteractionType,
	startTime: number,
	rate: number,
	labelStack: LabelStack | null,
	routeName?: string | null,
	trace: TraceIdContext | null = null,
	preloadKey?: string,
): void {
	postInteractionLog.reset();
	let vcObserver: VCObserverInterface | undefined;
	let previousTime = startTime;

	let timeoutTime = getInteractionTimeout(ufoName);
	const timerID: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
		abort(interactionId, 'timeout');
	}, timeoutTime);

	function changeTimeout(this: InteractionMetrics, newTime: number) {
		// we compare if the time left is lower than the new time to no
		// extend the timeout beyond the initial waiting time
		const currentTime = performance.now();
		const timeLeft = timeoutTime - (currentTime - previousTime);
		if (timeLeft < newTime) {
			return;
		}
		clearTimeout(this.timerID);
		const newTimerID: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
			abort(interactionId, 'timeout');
		}, newTime);
		timeoutTime = newTime;
		previousTime = currentTime;
		this.timerID = newTimerID;
	}

	const addFeatureFlagsToInteraction = coinflip(getCapabilityRate('feature_flag_access'));

	const config = getConfig();

	const searchPageConfig = {
		enableSmartAnswersMutations: config?.extraSearchPageInteraction?.enabled,
		searchPageRoute: config?.extraSearchPageInteraction?.searchPageRoute,
	};

	if (config && config.vc) {
		const vcOptions = {
			heatmapSize: config.vc.heatmapSize,
			oldDomUpdates: config.vc.oldDomUpdates,
			devToolsEnabled: config.vc.devToolsEnabled,
			// Use centralised getter so the FedRAMP override (forces every
			// selector field to false) is applied consistently.
			selectorConfig: getSelectorConfig(),
			ssrEnablePageLayoutPlaceholder: config.vc.ssrEnablePageLayoutPlaceholder,
			trackLayoutShiftOffenders: config.vc.trackLayoutShiftOffenders,
			searchPageConfig,
		};
		vcObserver = newVCObserver(vcOptions);
	}

	const priorAccessedFg = type === 'press' ? {} : Object.fromEntries(allFeatureFlagsAccessed);

	const metrics: InteractionMetrics = {
		id: interactionId,
		start: startTime,
		end: 0,
		ufoName,
		preloadKey,
		type,
		previousInteractionName: PreviousInteractionLog.name,
		isPreviousInteractionAborted: PreviousInteractionLog.isAborted === true,
		marks: [],
		customData: [],
		cohortingCustomData: new Map(),
		customTimings: [],
		spans: [],
		requestInfo: [],
		reactProfilerTimings: [],
		holdInfo: [],
		holdActive: new Map(),
		preloadInfo: [],
		// measure when we execute this code
		// from this, we can measure the input delay -
		// how long the browser took to hand execution back to JS)
		measureStart: performance.now(),
		rate,
		cancelCallbacks: [],
		metaData: {},
		excluded3pSegmentData: {},
		errors: [],
		apdex: [],
		labelStack,
		routeName: routeName ?? ufoName,
		featureFlags: addFeatureFlagsToInteraction
			? {
					prior: priorAccessedFg,
					during: {},
				}
			: undefined,
		knownSegments: [],
		cleanupCallbacks: [],
		awaitReactProfilerCount: 0,
		redirects: [],
		timerID,
		changeTimeout,
		trace,
		vcObserver,
		hold3pActive: new Map(),
		hold3pInfo: [],
		minorInteractions: [],
	};
	if (addFeatureFlagsToInteraction) {
		currentFeatureFlagsAccessed.clear();
	}
	interactions.set(interactionId, metrics);

	const segmentObserver: SegmentObserver = {
		onAdd(segment) {
			metrics.knownSegments.push(segment);
		},
		onRemove() {},
	};
	addSegmentObserver(segmentObserver);
	metrics.cleanupCallbacks.push(() => {
		removeSegmentObserver(segmentObserver);
	});
	metrics.cleanupCallbacks.push(() => {
		clearTimeout(metrics.timerID);
	});
	// Add cleanup for per-interaction VC observer
	if (vcObserver) {
		metrics.cleanupCallbacks.push(() => {
			vcObserver.stop(ufoName);
		});
	}
	const awaitBM3TTIList = getAwaitBM3TTIList();
	if (awaitBM3TTIList.includes(ufoName)) {
		addHoldByID(interactionId, [], ufoName, ufoName, true);
	}
	if (type === 'transition' || type === 'page_load') {
		// Use per-interaction VC observer if available, otherwise fall back to global
		const observer = vcObserver;
		if (observer) {
			observer.start({ startTime, experienceKey: ufoName });
		}
		// Start post interaction observer for all if config is enabled
		// in case ufoName is updated at later time
		if (getConfig()?.postInteractionLog?.enabled) {
			postInteractionLog.startVCObserver({ startTime });
		}
	}

	if (type === 'press') {
		// Use per-interaction VC observer if available, otherwise fall back to global
		const observer = vcObserver;
		if (observer) {
			observer.start({ startTime, experienceKey: ufoName });
		}
	}

	if (onInteractionStartForPreloadHolds) {
		try {
			onInteractionStartForPreloadHolds(interactionId, ufoName, preloadKey);
		} catch {
			// Preload instrumentation must not break interaction creation.
		}
	}
}

export function addBrowserMetricEvent(event: BM3Event): void {
	const interaction = getActiveInteraction();
	if (interaction) {
		interaction.legacyMetrics = interaction.legacyMetrics || [];
		interaction.legacyMetrics.push(event);

		if (
			(interaction.type === 'page_load' || interaction.type === 'transition') &&
			event.config?.type === 'PAGE_LOAD'
		) {
			removeHoldByID(interaction.id, interaction.ufoName);
		}
	}
}

export function addApdexToAll(apdex: ApdexType): void {
	interactions.forEach((interaction, key) => {
		interaction.apdex.push(apdex);
		try {
			// for Firefox 102 and older
			performance.measure(`🛸 ${apdex.key} [bm3_tti]`, {
				start: apdex.startTime ?? interaction.start,
				end: apdex.stopTime,
				detail: {
					devtools: {
						track: 'main metrics',
						trackGroup: '🛸 reactUFO metrics',
						color: 'primary-dark',
					},
				},
			});

			performance.mark(`[bm3] TTI`, {
				startTime: apdex.stopTime,
				detail: {
					devtools: {
						dataType: 'marker',
					},
				},
			});
		} catch {
			// do nothing
		}
		if (interaction.type === 'page_load' || interaction.type === 'transition') {
			removeHoldByID(key, interaction.ufoName);
		}
	});
}

export function addApdex(
	interactionId: string,
	apdexInfo: {
		key: string;
		stopTime: number;
		startTime?: number;
		labelStack?: LabelStack;
	},
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.apdex.push(apdexInfo);
		try {
			// for Firefox 102 and older
			performance.measure(`🛸 ${apdexInfo.key} [bm3_tti]`, {
				start: apdexInfo.startTime ?? interaction.start,
				end: apdexInfo.stopTime,
				detail: {
					devtools: {
						track: 'main metrics',
						trackGroup: '🛸 reactUFO metrics',
						color: 'primary-dark',
					},
				},
			});

			performance.mark(`[bm3] TTI`, {
				startTime: apdexInfo.stopTime,
				detail: {
					devtools: {
						dataType: 'marker',
					},
				},
			});
		} catch {
			// do nothing
		}
		if (interaction.type === 'page_load' || interaction.type === 'transition') {
			removeHoldByID(interactionId, interaction.ufoName);
		}
	}
}

export function addRequestInfo(
	interactionId: string,
	labelStack: LabelStack,
	requestInfo: RequestInfo,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.requestInfo.push({
			labelStack,
			...requestInfo,
		});
	}
}

function isSegmentLabel(obj: any): obj is SegmentLabel {
	return obj && typeof obj.name === 'string' && typeof obj.segmentId === 'string';
}

function getSegmentCacheKey(labelStack: LabelStack) {
	return labelStack
		.map((l) => {
			if (isSegmentLabel(l)) {
				return `${l.name}_${l.segmentId}`;
			}
			return l.name;
		})
		.join('|');
}

export function addSegment(labelStack: LabelStack): void {
	const key = getSegmentCacheKey(labelStack);
	const existingSegment = segmentCache.get(key);
	if (!existingSegment) {
		const segmentInfo: SegmentInfo = { labelStack };
		segmentCache.set(key, segmentInfo);
		segmentObservers.forEach((observer) => {
			observer.onAdd(segmentInfo);
		});
	}
}

export function removeSegment(labelStack: LabelStack): void {
	const key = getSegmentCacheKey(labelStack);
	const segmentInfo = segmentCache.get(key);

	if (segmentInfo) {
		segmentCache.delete(JSON.stringify(labelStack));

		const cacheKey = stringifyLabelStackFully(labelStack);
		segmentUnmountCache.set(cacheKey, (segmentUnmountCache.get(cacheKey) || 0) + 1);

		segmentObservers.forEach((observer) => {
			observer.onRemove(segmentInfo);
		});
	}
}

export function addRedirect(
	interactionId: string,
	fromUfoName: string,
	nextUfoName: string,
	nextRouteName: string,
	time: number,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.ufoName = nextUfoName;
		interaction.routeName = nextRouteName;
		interaction.redirects.push({ fromInteractionName: fromUfoName, time });

		if (isPerformanceTracingEnabled()) {
			const prevRedirect = interaction.redirects.at(-2);
			try {
				// for Firefox 102 and older
				performance.measure(`🛸 ${nextUfoName} [redirect]`, {
					start: prevRedirect?.time ?? interaction.start,
					end: time,
					detail: {
						devtools: {
							track: '🛸 reactUFO detailed timings',
							color: 'tertiary',
						},
					},
				});
			} catch {
				// do nothing
			}
		}
	}
}

declare global {
	interface Window {
		__REACT_UFO_ENABLE_PERF_TRACING?: boolean;
		__UFO_COMPACT_PAYLOAD__?: boolean;
		__CRITERION__?: {
			addFeatureFlagAccessed?: (flagName: string, flagValue: FeatureFlagValue) => void;
			addUFOHold?: (id: string, name: string, labelStack: string, startTime: number) => void;
			removeUFOHold?: (id: string) => void;
			getFeatureFlagOverride?: (flagName: string) => boolean | undefined;
			getExperimentValueOverride?: <T>(experimentName: string, parameterName: string) => T;
		};
	}
}

export const interactionSpans: Span[] = [];

const defaultLabelStack = [{ name: 'custom' }];

export function addCustomSpans(
	name: string,
	start: number,
	end: number = performance.now(),
	size = 0,
	labelStack: LabelStack = defaultLabelStack,
): void {
	const customSpan: Span = {
		type: 'custom',
		name,
		start,
		end,
		labelStack,
		size,
	};

	interactionSpans.push(customSpan);
}
